import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";

import api from "../../../services/api";
import { all_routes } from "../../router/all_routes";

interface ReminderItem {
  reminder_id: number;
  lead_id: number;
  lead_name: string;
  mobile_no?: string;
  pipeline_char?: string;
  form_name?: string;
  follow_up_date?: string;
  bucket: "today" | "overdue";
}

interface TeamMember {
  user_id: number;
  name: string;
  todayCount: number;
  overdueCount: number;
}

interface DigestData {
  role: "employee" | "lead";
  today?: ReminderItem[];
  overdue?: ReminderItem[];
  team?: TeamMember[];
  totalToday?: number;
  totalOverdue?: number;
}

const isCrmModule = (search: string): boolean =>
  new URLSearchParams(search).get("module") === "crm";

const istHour = (): number => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  return Number(parts.find((p) => p.type === "hour")?.value ?? "0");
};

const istDate = (): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const currentPeriod = (): "P1" | "P2" | "P3" | null => {
  const h = istHour();
  if (h >= 10 && h < 12) return "P1";
  if (h >= 12 && h < 15) return "P2";
  if (h >= 15 && h < 18) return "P3";
  return null;
};

const formatDate = (value?: string): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(d);
};

const LeadRemindersPopup: React.FC = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<DigestData | null>(null);
  const fetchingRef = useRef(false);
  const handledPeriodRef = useRef<string | null>(null);
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const readyRef = useRef(false);

  const maybeShow = useCallback(async () => {
    if (!readyRef.current) return;
    const signature = localStorage.getItem("signature");
    if (!signature) return;
    if (!isCrmModule(searchRef.current)) return;

    const period = currentPeriod();
    if (!period) return;
    const periodKey = `${istDate()}:${period}`;
    if (handledPeriodRef.current === periodKey) return;
    if (fetchingRef.current || visibleRef.current) return;

    fetchingRef.current = true;
    try {
      const res = await api.post("leadService/getLeadRemindersDigest", {
        signature,
      });
      const payload = res?.data?.message;
      if (payload?.message === "Data Found") {
        handledPeriodRef.current = periodKey;
        if (!payload.seen) {
          const hasContent =
            payload.role === "lead"
              ? (payload.team?.length || 0) > 0
              : (payload.totalToday || 0) + (payload.totalOverdue || 0) > 0;
          if (hasContent) {
            setData(payload);
            setVisible(true);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching lead reminders digest:", err);
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const initial = setTimeout(() => {
      readyRef.current = true;
      maybeShow();
    }, 90 * 1000);
    const interval = setInterval(maybeShow, 5 * 60 * 1000);
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [maybeShow]);

  useEffect(() => {
    if (!isCrmModule(location.search)) {
      setVisible(false);
      return;
    }
    maybeShow();
  }, [location.pathname, location.search, maybeShow]);

  const handleClose = () => setVisible(false);

  if (!visible || !data) return null;

  const isLead = data.role === "lead";
  const items: ReminderItem[] = isLead
    ? []
    : [...(data.overdue || []), ...(data.today || [])];

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 9998,
        width: 380,
        maxWidth: "92vw",
        background: "#ffffff",
        borderRadius: 14,
        boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        animation: "lr-slide-up 360ms ease-out",
      }}
    >
      <style>{`@keyframes lr-slide-up{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

      <div
        style={{
          padding: "14px 16px",
          background: "linear-gradient(135deg,#4f46e5,#6366f1)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className="ti ti-bell" style={{ fontSize: 18 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {isLead ? "Team Reminders" : "Your Reminders"}
            </div>
            <div style={{ fontSize: 11.5, opacity: 0.9 }}>
              {(data.totalOverdue || 0)} overdue · {(data.totalToday || 0)} today
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          <i className="ti ti-x" />
        </button>
      </div>

      <div style={{ maxHeight: "56vh", overflowY: "auto", padding: "8px 8px 4px" }}>
        {isLead ? (
          (data.team || []).map((m) => (
            <div
              key={m.user_id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 10px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ fontSize: 13.5, fontWeight: 500, color: "#0f172a" }}>
                {m.name}
              </span>
              <span style={{ display: "flex", gap: 6 }}>
                {m.overdueCount > 0 && (
                  <span style={badge("#fee2e2", "#b91c1c")}>
                    {m.overdueCount} overdue
                  </span>
                )}
                {m.todayCount > 0 && (
                  <span style={badge("#dbeafe", "#1d4ed8")}>
                    {m.todayCount} today
                  </span>
                )}
              </span>
            </div>
          ))
        ) : (
          items.map((it) => (
            <div
              key={`${it.lead_id}-${it.reminder_id}`}
              style={{ padding: "10px 10px", borderBottom: "1px solid #f1f5f9" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {it.lead_name || "N/A"}
                </span>
                <span
                  style={
                    it.bucket === "overdue"
                      ? badge("#fee2e2", "#b91c1c")
                      : badge("#dbeafe", "#1d4ed8")
                  }
                >
                  {it.bucket === "overdue" ? "Overdue" : "Today"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 4,
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                <span>{it.mobile_no || "—"}</span>
                <span>{formatDate(it.follow_up_date)}</span>
              </div>
              {it.pipeline_char && (
                <div style={{ marginTop: 3, fontSize: 11.5, color: "#94a3b8" }}>
                  {it.pipeline_char}
                  {it.form_name ? ` · ${it.form_name}` : ""}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0" }}>
        <Link
          to={`${all_routes.leadsGrid}?module=crm`}
          rel="noopener noreferrer"
          onClick={() => setVisible(false)}
          style={{
            display: "block",
            width: "100%",
            padding: "9px 12px",
            background: "#4f46e5",
            color: "#fff",
            borderRadius: 8,
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "center",
            textDecoration: "none",
          }}
        >
          <i className="ti ti-external-link me-1" />
          View in Leads
        </Link>
      </div>
    </div>
  );
};

const badge = (bg: string, color: string): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

export default LeadRemindersPopup;
